/**
 * CAREER MENU and the Aerodrome behind it, as a state machine.
 *
 * The captured screens are the spine: `career-menu.png`'s four-button board,
 * the side picker, `career-enlist-name.png`'s name entry with its live
 * enlistment sentence, then `mission-assignment.png` → `flight-assignments.png`
 * → the sortie, and `safe-return.png` → `mission-debriefing.png` on the way
 * back. Everything that *decides* anything still lives where it did — the
 * career store in `career/career-store.ts`, the campaign rules in
 * `packages/engine` — and this module only says which screen is on and what
 * the player has typed into it.
 *
 * Two screens have no capture and are therefore stated as inference rather
 * than measurement: the AERODROME menu (documented in `ui-menus.md` as an
 * eight-item list, which does not fit one 320×200 column under a banner, so
 * it is drawn on the wide two-column board `fly-single-mission.png` measures)
 * and VIEW PILOT RECORD (a full-bleed panel of the field list `ui-menus.md`
 * quotes verbatim).
 */

import type { EnlistmentPeriod, Nation } from "@red-opus/engine";
import type { CareerCutsceneKind } from "../../career/career-cutscenes.ts";
import type { RealismPanel } from "../../mission/realism-settings.ts";
import type { ShellEffect } from "./menu-model.ts";
import { aerodromeStartDialog, sidePickerDialog } from "./mission-dialogs.ts";
import { clampZoom, INITIAL_POSE, YAW_STEP_DEG, type PreviewPose } from "../../career/preview-pose.ts";
import type { PaintPart } from "./paint-screens.ts";
import type { ShellDialog } from "./shell-screen.ts";

export type CareerView =
  | "menu"
  | "roster"
  | "topAces"
  | "enlist"
  | "backup"
  | "aerodrome"
  | "record"
  | "assignment"
  | "flight"
  | "safeReturn"
  | "debrief"
  | "consequences"
  | "realism"
  | "aircraft"
  | "requestAircraft"
  | "transfer"
  | "paintPart"
  | "paintColour";

export const MAX_PILOT_NAME = 20;

export interface CareerModel {
  readonly view: CareerView;
  readonly nation: Nation;
  readonly period: EnlistmentPeriod;
  readonly name: string;
  /** Selection and scroll for whichever list box is on screen. */
  readonly listIndex: number;
  readonly listTop: number;
  readonly realism: RealismPanel;
  readonly dialog: ShellDialog | undefined;
  /**
   * The cursor inside whichever dialog is up, kept *beside* the dialog rather
   * than inside it, because one of them is not stored at all: the consequences
   * popup is rebuilt from the save on every paint (`career-surface.ts`'s
   * `consequenceDialog`). The runtime moves a dialog's focus by handing the
   * surface a modified copy back, and storing that copy would freeze a derived
   * dialog's *content* along with its cursor — see `setDialog`'s note, which is
   * B5.
   */
  readonly dialogFocus: number;
  readonly focus: number;
  readonly realismReturn: CareerView;
  /** Which of the sortie's consequences is on screen, once the debriefing has been read. */
  readonly consequence: number;
  /**
   * Which page of the `SIMEND.DAT` outcome sequence is on screen, before the
   * debriefing. Most outcomes are one page; the hospital and prison-camp ones
   * run to four. `go` rewinds it with every other cursor, so a pilot always
   * arrives at his next sortie's ending on its first page.
   */
  readonly outcomePage: number;
  /**
   * Career-milestone cutscenes this sortie earned, still to play, front
   * first — `career-surface.ts` fills this from `lastConsequence` the moment
   * `"consequences"` fires (a pure model has no store to read it from) and
   * pops it as each one finishes; the consequences dialog itself doesn't
   * paint until this drains, so a scene always plays before its citation
   * text does.
   */
  readonly cutsceneQueue: readonly CareerCutsceneKind[];
  /** What `PAINT WHAT?` picked, carried into `WHICH COLOR?`. */
  readonly paintPart: PaintPart;
  /** The PERSONAL AIRCRAFT turntable's heading and zoom, driven by the four view arrows. */
  readonly preview: PreviewPose;
}

export function initialCareer(realism: RealismPanel, resumed: boolean): CareerModel {
  return {
    view: resumed ? "aerodrome" : "menu",
    nation: "allied",
    period: "middle",
    name: "",
    listIndex: 0,
    listTop: 0,
    realism,
    dialog: undefined,
    dialogFocus: 0,
    focus: 0,
    realismReturn: "aerodrome",
    consequence: 0,
    outcomePage: 0,
    cutsceneQueue: [],
    paintPart: "everything",
    preview: INITIAL_POSE,
  };
}

export interface CareerTransition {
  readonly model: CareerModel;
  readonly effect: ShellEffect | undefined;
}

function stay(model: CareerModel): CareerTransition {
  return { model, effect: undefined };
}

/**
 * Opening a screen rewinds its cursor. `listIndex`/`listTop` are shared by every
 * list box the surface has — the pilot roster, TOP ACES, SELECT AIRCRAFT and now
 * `Pick a Squadron:` — so arriving at one of them with a sibling's row 7 still
 * selected points the ACCEPT button at nothing, or at the wrong thing.
 */
function go(model: CareerModel, view: CareerView): CareerTransition {
  return stay({ ...model, view, focus: 0, dialog: undefined, dialogFocus: 0, listIndex: 0, listTop: 0, outcomePage: 0 });
}

/** The three enlistment dates cycle on the boxed `[in the middle of the war]` slot, exactly as the capture's clickable template value implies. */
const PERIOD_CYCLE: readonly EnlistmentPeriod[] = ["early", "middle", "late"];

function nextPeriod(period: EnlistmentPeriod): EnlistmentPeriod {
  const index = PERIOD_CYCLE.indexOf(period);
  return PERIOD_CYCLE[(index + 1) % PERIOD_CYCLE.length] ?? "middle";
}

/**
 * Typing into `WHAT IS YOUR PILOT'S NAME?` — and into BACKUP CAREER's
 * `ENTER BACKUP PILOT NAME?`, which is the only other screen in `/career` with
 * a field. Both write `name`, because they are never on screen at once and the
 * command that opens either one clears it.
 */
export function typeName(model: CareerModel, key: string): CareerModel {
  if ((model.view !== "enlist" && model.view !== "backup") || model.dialog) return model;
  if (key === "Backspace") return { ...model, name: model.name.slice(0, -1) };
  if (key.length !== 1 || key < " " || model.name.length >= MAX_PILOT_NAME) return model;
  return { ...model, name: model.name + key };
}

/**
 * A view command opens a screen and nothing else. Anything missing from this
 * table falls through to `stay(model)` and the button silently does nothing,
 * which is what `REQUEST TRANSFER` and `REQUEST NEW AIRCRAFT` both did until
 * G31 — `shell-flow.test.ts` now walks every AERODROME button and asserts the
 * screen moves, so a new dead button fails a test rather than a play-through.
 */
const VIEW_COMMANDS: Readonly<Record<string, CareerView>> = {
  roster: "roster",
  "top-aces": "topAces",
  aerodrome: "aerodrome",
  aircraft: "aircraft",
  transfer: "transfer",
  "request-aircraft": "requestAircraft",
  paint: "paintPart",
  record: "record",
  assignment: "assignment",
  flight: "flight",
  debrief: "debrief",
  menu: "menu",
};

function listCommand(model: CareerModel, command: string): CareerTransition | undefined {
  if (!command.startsWith("row:")) return undefined;
  const index = Number.parseInt(command.slice(4), 10);
  return Number.isFinite(index) ? stay({ ...model, listIndex: index }) : stay(model);
}

/**
 * The pure half of the surface. Commands that touch the career store —
 * enlisting, selecting, deleting, flying, reporting back for duty — are
 * intercepted by the route before they reach here, because those need a
 * `CareerStore` and this module deliberately has none.
 */
export function careerCommand(model: CareerModel, command: string): CareerTransition {
  const list = listCommand(model, command);
  if (list) return list;
  if (command === "start-career") return stay({ ...model, name: "", dialog: sidePickerDialog() });
  // BACKUP CAREER shares `name` with the enlistment field, so opening it starts
  // the field empty rather than pre-filled with whatever was typed last.
  if (command === "backup") return go({ ...model, name: "" }, "backup");
  if (command === "side:german" || command === "side:allied") {
    return stay({ ...model, nation: command === "side:german" ? "german" : "allied", view: "enlist", focus: 0, dialog: undefined });
  }
  if (command === "period") return stay({ ...model, period: nextPeriod(model.period) });
  if (command === "close") return stay({ ...model, dialog: undefined });
  if (command === "return") return { model, effect: { kind: "navigate", path: "/" } };
  if (command === "aerodrome-start-dialog") {
    return stay({ ...model, dialog: aerodromeStartDialog(model.realism.aerodromeStart) });
  }
  if (command === "realism") return go({ ...model, realismReturn: model.view }, "realism");
  if (command === "realism-accept") return go(model, model.realismReturn);
  if (command === "safe-return") return go(model, "safeReturn");
  // One CONTINUE, one page of the outcome sequence. The last page's button is
  // `debrief` instead, so this never runs off the end of the sequence.
  if (command === "outcome-next") return stay({ ...model, outcomePage: model.outcomePage + 1 });
  if (command === "consequences") return stay({ ...model, view: "consequences", consequence: 0, dialogFocus: 0 });
  if (command === "next-consequence") return stay({ ...model, consequence: model.consequence + 1, dialogFocus: 0 });
  const view = viewCommand(model, command);
  if (view) return view;
  const next = VIEW_COMMANDS[command];
  return next ? go(model, next) : stay(model);
}

/**
 * The turntable's four arrows — *"the arrow keys on the bottom left of the
 * screen allow you to change the view of your aircraft"*. Pure: the pose is
 * model state, and `career/aircraft-preview.ts` only ever reads it.
 */
function viewCommand(model: CareerModel, command: string): CareerTransition | undefined {
  if (!command.startsWith("view:")) return undefined;
  const pose = model.preview;
  const moved: Readonly<Record<string, PreviewPose>> = {
    "view:rotate-left": { ...pose, yawDeg: (pose.yawDeg - YAW_STEP_DEG + 360) % 360 },
    "view:rotate-right": { ...pose, yawDeg: (pose.yawDeg + YAW_STEP_DEG) % 360 },
    "view:zoom-in": { ...pose, zoom: clampZoom(pose.zoom - 1) },
    "view:zoom-out": { ...pose, zoom: clampZoom(pose.zoom + 1) },
  };
  const next = moved[command];
  return next ? stay({ ...model, preview: next }) : stay(model);
}
